import React, { createContext, useContext, ReactNode } from 'react';
import { GeocodingService } from '../services/geocoding.service';
import { GeocodingCache } from '../services/geocoding-cache.service';

interface GeocodingContextType {
  service: GeocodingService;
  clearCache: () => void;
  preloadAddresses: (coordinates: Array<{ lat: number; lng: number }>) => void;
}

const GeocodingContext = createContext<GeocodingContextType | null>(null);

export const useGeocodingService = () => {
  const context = useContext(GeocodingContext);
  if (!context) {
    throw new Error('useGeocodingService must be used within GeocodingProvider');
  }
  return context;
};

interface GeocodingProviderProps {
  children: ReactNode;
}

export const GeocodingProvider: React.FC<GeocodingProviderProps> = ({ children }) => {
  const service = GeocodingService.getInstance();

  const clearCache = () => {
    GeocodingCache.clear();
  };

  const preloadAddresses = async (coordinates: Array<{ lat: number; lng: number }>) => {
    // Filter out already cached coordinates
    const uncached = coordinates.filter(
      coord => !GeocodingCache.isCached(coord.lat, coord.lng)
    );

    if (uncached.length === 0) {
      return;
    }

    // Load with low priority
    const promises = uncached.map(coord =>
      service.getAddress(coord.lat, coord.lng, -1) // Low priority
    );

    await Promise.allSettled(promises);
  };

  const value: GeocodingContextType = {
    service,
    clearCache,
    preloadAddresses
  };

  return (
    <GeocodingContext.Provider value={value}>
      {children}
    </GeocodingContext.Provider>
  );
};